// v51 — Auth loading screen.
//
// REPLACED 2026-08-24. This used to be a wireframe: a bordered sidebar
// rectangle beside a bordered content rectangle, filled with pulsing grey
// pills. The intent was to reduce layout shift on hydration. In practice it
// read as a broken page — two hard-ruled boxes around empty space, with
// nothing to say the app was doing anything.
//
// It also had a second problem: the version string was hardcoded to "v49.9"
// and had been wrong for months.
//
// WHY THIS MATCHES index.html RATHER THAN THE APP SHELL
//
// A user hard-reloading sees the HTML splash first, then this. If the two look
// different, one load looks like two — a flash of animation, then a flash of
// wireframe. Matching them makes it read as a single continuous wait, which is
// what it actually is.
//
// The markup is deliberately styled with a local <style> block rather than
// utility classes: this repo has no Tailwind build (src/index.css is a
// pre-compiled artefact), and a loading screen is the one component that must
// not depend on a class existing. The InvoiceView / BidPackPrintView pattern.
//
// WHY THE DOTS ANIMATE
//
// A static "Loading" gives the user no way to tell a slow request from a hung
// one. Three sequenced dots say "still working" continuously and cost nothing.
// They are aria-hidden and absolutely positioned: a screen reader hears
// "Loading" once rather than a stream of punctuation, and the centred label
// does not reflow as they appear.

export function AuthLoadingSkeleton() {
  return (
    <div className="lc-auth-splash" role="status" aria-live="polite" aria-label="Checking your session">
      <style>{`
        .lc-auth-splash {
          position: fixed;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #ffffff;
        }
        .lc-auth-splash__inner {
          width: min(320px, 72vw);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 26px;
        }
        .lc-auth-splash__mark {
          width: min(260px, 56vw);
          height: auto;
        }
        .lc-auth-splash__bar {
          width: 132px;
          height: 3px;
          border-radius: 999px;
          background: #e6e9ef;
          overflow: hidden;
        }
        .lc-auth-splash__bar > span {
          display: block;
          width: 40%;
          height: 100%;
          border-radius: 999px;
          background: #1f6feb;
          animation: lc-auth-splash-slide 1150ms ease-in-out infinite;
        }
        @keyframes lc-auth-splash-slide {
          0%   { transform: translateX(-110%); }
          100% { transform: translateX(360%); }
        }
        .lc-auth-splash__word {
          font: 600 12px/1 ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #8b95a6;
          /* The dots are absolutely positioned after the word, so the line does
             NOT reflow as they appear. A centred label that shifts left and
             right three times a second reads as jitter, not as progress. */
          position: relative;
        }
        .lc-auth-splash__dots {
          position: absolute;
          left: 100%;
          margin-left: 3px;
          display: inline-flex;
          gap: 2px;
        }
        .lc-auth-splash__dots i {
          font-style: normal;
          opacity: 0;
          animation: lc-dot 1400ms ease-in-out infinite;
        }
        .lc-auth-splash__dots i:nth-child(2) { animation-delay: 200ms; }
        .lc-auth-splash__dots i:nth-child(3) { animation-delay: 400ms; }
        /* One dot lights, then the next, then the third; all three fade
           together and the cycle restarts — the word appears to "breathe"
           rather than the dots merely toggling. */
        @keyframes lc-dot {
          0%, 15%   { opacity: 0; }
          30%, 70%  { opacity: 1; }
          85%, 100% { opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .lc-auth-splash__bar > span { animation: none; width: 100%; }
          /* Motion off: show all three, steady. The label must still read as a
             complete sentence for anyone who cannot see the animation. */
          .lc-auth-splash__dots i { animation: none; opacity: 1; }
        }
      `}</style>

      <div className="lc-auth-splash__inner">
        <img
          className="lc-auth-splash__mark"
          src="/brand/labourcore-logo-stacked.png"
          alt="LabourCore"
        />
        <div className="lc-auth-splash__bar"><span /></div>
        {/* "Loading", not "Checking your session".
            A hard reload paints the HTML splash in index.html first and this
            one second. When the two say different things, one continuous wait
            reads as two separate loads — which is the bug this file's header
            warns about and which the wording had drifted into anyway. */}
        <div className="lc-auth-splash__word">
          Loading
          <span className="lc-auth-splash__dots" aria-hidden="true">
            <i>.</i><i>.</i><i>.</i>
          </span>
        </div>
      </div>
    </div>
  );
}
